import { useToast } from 'primevue/usetoast';

let lastApiErrorMessage = '';
let lastApiErrorAt = 0;
const API_ERROR_DEDUPLICATION_MS = 3000;

export function shouldShowApiError(message, now = Date.now()) {
    if (message === lastApiErrorMessage && now - lastApiErrorAt < API_ERROR_DEDUPLICATION_MS) return false;
    lastApiErrorMessage = message;
    lastApiErrorAt = now;
    return true;
}

/**
 * Composable for managing notifications throughout the application
 * Provides a consistent API for showing success, error, warning, and info messages
 */
export function useNotifications() {
    const toast = useToast();
    
    /**
     * Show a success notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional options
     * @param {string} options.title - Custom title (default: 'Success')
     * @param {number} options.duration - Duration in ms (default: 5000)
     */
    const success = (message, options = {}) => {
        toast.add({
            severity: 'success',
            summary: options.title || 'Success',
            detail: message,
            life: options.duration || 5000,
            ...options
        });
    };
    
    /**
     * Show an error notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional options
     * @param {string} options.title - Custom title (default: 'Error')
     * @param {number} options.duration - Duration in ms (default: 8000)
     */
    const error = (message, options = {}) => {
        toast.add({
            severity: 'error',
            summary: options.title || 'Error',
            detail: message,
            life: options.duration || 8000,
            ...options
        });
    };
    
    /**
     * Show a warning notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional options
     * @param {string} options.title - Custom title (default: 'Warning')
     * @param {number} options.duration - Duration in ms (default: 6000)
     */
    const warning = (message, options = {}) => {
        toast.add({
            severity: 'warn',
            summary: options.title || 'Warning',
            detail: message,
            life: options.duration || 6000,
            ...options
        });
    };


    
    /**
     * Show an info notification
     * @param {string} message - The message to display
     * @param {Object} options - Additional options
     * @param {string} options.title - Custom title (default: 'Info')
     * @param {number} options.duration - Duration in ms (default: 5000)
     */
    const info = (message, options = {}) => {
        toast.add({
            severity: 'info',
            summary: options.title || 'Info',
            detail: message,
            life: options.duration || 5000,
            ...options
        });
    };
    
    /**
     * Show a custom notification with full control
     * @param {Object} options - Toast options
     * @param {string} options.severity - 'success', 'error', 'warn', 'info'
     * @param {string} options.summary - Title of the notification
     * @param {string} options.detail - Message content
     * @param {number} options.life - Duration in ms
     */
    const show = (options) => {
        toast.add({
            life: 5000,
            ...options
        });
    };
    
    /**
     * Clear all notifications
     */
    const clear = () => {
        toast.removeAllGroups();
    };
    
    /**
     * Show API error notification with proper error handling
     * @param {Error|string} error - Error object or message
     * @param {string} defaultMessage - Default message if error parsing fails
     */
    const apiError = (error, defaultMessage = 'An error occurred') => {
        let message = defaultMessage;
        
        if (typeof error === 'string') {
            message = error;
        } else if (error?.message) {
            message = error.message;
        } else if (error?.response?.data?.error) {
            message = error.response.data.error;
        } else if (error?.response?.data?.message) {
            message = error.response.data.message;
        }
        
        if (!shouldShowApiError(message)) return;

        toast.add({
            severity: 'error',
            summary: 'API Error',
            detail: message,
            life: 8000
        });
    };
    
    /**
     * Show validation error notification
     * @param {string|Array} errors - Validation error(s)
     */
    const validationError = (errors) => {
        const message = Array.isArray(errors) ? errors.join(', ') : errors;
        
        toast.add({
            severity: 'error',
            summary: 'Validation Error',
            detail: message,
            life: 6000
        });
    };
    
    /**
     * Show operation success notification with action feedback
     * @param {string} operation - The operation that was performed
     * @param {string} entity - The entity that was affected (optional)
     */
    const operationSuccess = (operation, entity = '') => {
        const message = entity 
            ? `${entity} ${operation} successfully`
            : `${operation} completed successfully`;
            
        toast.add({
            severity: 'success',
            summary: 'Success',
            detail: message,
            life: 4000
        });
    };
    
    return {
        success,
        error,
        warning,
        info,
        show,
        clear,
        apiError,
        validationError,
        operationSuccess
    };
}

// Export notification types for consistency
export const NotificationTypes = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warn',
    INFO: 'info'
};

// Export default durations
export const NotificationDurations = {
    SHORT: 3000,
    MEDIUM: 5000,
    LONG: 8000,
    PERSISTENT: 0 // Won't auto-close
};
